import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Radio, Rss } from "lucide-react";
import Wordmark from "./Wordmark";
import { useConsent } from "../lib/consent";
import LanguageSwitcher from "./LanguageSwitcher";
import SocialRow from "./SocialRow";

const METHOD_VERSION = "qortrace-method-v0.1";
// Iter269.9 — Module-scoped backend URL. The footer NewsletterChip
// referenced `API` without declaring it locally; the only `API` lived
// inside the `JustShipped` useEffect, so the chip was building
// `undefined/api/...` and silently throwing inside axios. Lifting the
// constant to module scope fixes both NewsletterChip + JustShipped.
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Footer() {
  const { openSettings } = useConsent();
  const { t } = useTranslation();

  return (
    <footer
      data-testid="site-footer"
      role="contentinfo"
      aria-label="Site footer"
      className="qt-no-print mt-24 border-t relative"
      style={{
        borderColor: "var(--qt-cyan)", borderTopWidth: "1px",
        boxShadow: "0 -1px 12px rgba(0, 229, 255, 0.18)",
        background: "var(--qt-bg)",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12">
        {/* Main footer grid ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <Wordmark size={22} />
            <p className="mt-4 text-base"
               data-testid="footer-subtitle"
               style={{ color: "var(--qt-text-2)", maxWidth: 420 }}>
              The cryptographic risk, intelligence and defense platform for
              blockchains, DeFi, exchanges, custodians, banks, governments,
              and the institutions holding the keys. Identify, score, monitor,
              and migrate before quantum-era threats become operational.
            </p>

            <div className="mt-5 flex gap-2 flex-wrap"
                 data-testid="footer-trust-chips">
              <TrustChip>NIST FIPS 203 / 204 / 205</TrustChip>
              <TrustChip>ML-DSA · SLH-DSA</TrustChip>
              <TrustChip>SOC 2 · IN PROGRESS</TrustChip>
              <TrustChip>GDPR · CCPA</TrustChip>
            </div>

            {/* Iter312 — "Just shipped" ribbon. Auto-syncs from the
                immutable audit log via /api/public/release-markers so
                the footer is always current without a code change.
                Falls back gracefully to the bundled defaults if the
                backend is unreachable. */}
            <JustShipped />

            {/* Iter275.3 — Newsletter signup chip ─────────────────── */}
            <NewsletterChip />
          </div>

          <FooterCol title="Products">
            <FooterLink to="/threat-radar" badge="LIVE">Threat Radar</FooterLink>
            <FooterLink to="/threat-radar/exposed-wallets" badge="NEW">Onchain HNDL Tracker</FooterLink>
            <FooterLink to="/scan">QorTrace Scan</FooterLink>
            <FooterLink to="/scan/bulk">Bulk HNDL Scanner</FooterLink>
            <FooterLink to="/contract">QorTrace Contract</FooterLink>
            <FooterLink to="/atlas">QorTrace Atlas</FooterLink>
            <FooterLink to="/audit">PQC Audit Suite</FooterLink>
            <FooterLink to="/pricing/monitoring">MPC Vendor Matrix</FooterLink>
            <FooterLink to="/labs">QorTrace Labs</FooterLink>
            <FooterLink to="/methodology">Methodology</FooterLink>
            <FooterLink to="https://qorbom.com/"
                        external
                        testid="footer-link-qorbom-home"
                        badge="NEW">QorBOM — for audit firms</FooterLink>
            <FooterLink to="https://qorbom.com/api-reference"
                        external
                        testid="footer-link-qorbom-api"
                        badge="LIVE">QorBOM API</FooterLink>
            <FooterLink to="/account/monitoring"
                        testid="footer-link-monitoring"
                        badge="LIVE">Continuous Monitoring</FooterLink>
          </FooterCol>

          <FooterCol title="Roadmap">
            <FooterMuted>SOC 2 Type II Evidence Pack</FooterMuted>
            <FooterMuted>QorScore™ Public Beta</FooterMuted>
            <FooterMuted>The QorIndex™ Aggregate</FooterMuted>
            <FooterMuted>QorChain Provenance Ledger</FooterMuted>
            <FooterMuted>Fly.io Sovereign Hosting</FooterMuted>
          </FooterCol>

          <FooterCol title="Resources">
            <FooterLink to="/verify">Verify an Audit</FooterLink>
            <FooterLink to="/trust">Trust Center</FooterLink>
            <FooterLink to="/account/nda?view=create" testid="footer-link-nda">Mutual NDA</FooterLink>
            <FooterLink to="/status">Service Status</FooterLink>
            <FooterLink to="/whats-new">What's New</FooterLink>
            <FooterLink to="/docs/qelli-free-with-account"
                        testid="footer-link-qelli">Qelli — free AI</FooterLink>
            <FooterLink to="/legal/security">Security &amp; Disclosure</FooterLink>
            <FooterLink to="/threat-model"
                        testid="footer-link-threat-model">Threat Model</FooterLink>
            <FooterLink to="/audit/talk-to-sales"
                        testid="footer-link-contact">Contact Sales</FooterLink>
          </FooterCol>

          <FooterCol title="Legal">
            <FooterLink to="/legal/terms">Terms of Service</FooterLink>
            <FooterLink to="/legal/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/legal/cookies">Cookie Policy</FooterLink>
            <li className="text-base leading-tight">
              <button
                type="button"
                onClick={openSettings}
                data-testid="footer-cookie-settings-btn"
                className="inline-block transition-colors text-left"
                style={{
                  color: "var(--qt-text-2)", lineHeight: "1.4",
                  background: "transparent", border: 0, padding: 0,
                  cursor: "pointer", font: "inherit",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#FFFFFF")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-2)")}
              >
                Cookie settings
              </button>
            </li>
          </FooterCol>
        </div>

        {/* Phase 12.6 — Sister Brands row. Bumped from discreet ribbon
            to a proper labeled section with a link to the dedicated
            /sister-brands hub. Each brand below has its own domain +
            buyer + product story. */}
        <div className="mt-10 pt-6 border-t"
             style={{ borderColor: "var(--qt-border)" }}
             data-testid="footer-sister-brands">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
            <div className="qt-mono text-[11px]"
                 style={{ color: "var(--qt-cyan)",
                          letterSpacing: "0.18em", fontWeight: 700 }}>
              THE QOR ECOSYSTEM
            </div>
            <Link to="/sister-brands"
                  data-testid="footer-sister-brands-hub-link"
                  className="qt-mono text-[10px] inline-flex items-center gap-1 transition-colors"
                  style={{ color: "var(--qt-text-3)",
                           letterSpacing: "0.10em",
                           textDecoration: "underline" }}>
              ALL BRANDS
              <ArrowRight size={10} />
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <a
              href="https://qorbom.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-sister-brand-qorbom"
              className="inline-flex items-center gap-2 transition-colors"
              style={{ color: "var(--qt-text-1)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--qt-cyan)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-1)")}
            >
              <span style={{ fontFamily: "Manrope, sans-serif",
                             fontWeight: 700, fontSize: 14 }}>
                QorBOM<span style={{ verticalAlign: "super", fontSize: 9,
                                     color: "var(--qt-cyan)" }}>™</span>
              </span>
              <span className="qt-mono text-[10px]"
                    style={{ color: "var(--qt-text-3)",
                             letterSpacing: "0.04em" }}>
                qorbom.com · CBOM platform for audit firms
              </span>
              <span className="qt-mono text-[9px] px-1.5 py-0.5 rounded"
                    style={{ background: "rgba(31,221,131,0.15)",
                             color: "#1FDD83",
                             letterSpacing: "0.16em",
                             fontWeight: 700 }}>
                LIVE
              </span>
            </a>
          </div>
        </div>

        {/* Iter273.5 — Beta-tester #20 + #35: discoverable RSS row.
            Surfaces the two RSS feeds (Dev releases / Qelli News Radar)
            globally on every page instead of only on /docs/changelog
            and the authed dashboard. */}
        <div className="mt-10 pt-6 border-t"
             style={{ borderColor: "var(--qt-border)" }}
             data-testid="footer-rss-feeds">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
            <div className="qt-mono text-[11px]"
                 style={{ color: "var(--qt-cyan)",
                          letterSpacing: "0.18em", fontWeight: 700 }}>
              STAY IN SYNC · RSS FEEDS
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <a
              href={`${API}/api/docs/changelog.rss`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-rss-changelog"
              title="Subscribe to QorTrace dev release notes in your feed reader"
              className="inline-flex items-center gap-2 qt-mono text-[12px] transition-colors"
              style={{ color: "var(--qt-text-1)", textDecoration: "none",
                       letterSpacing: "0.04em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--qt-cyan)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-1)")}
            >
              <Rss size={12} />
              <span>Dev releases</span>
              <span className="qt-mono text-[10px]"
                    style={{ color: "var(--qt-text-3)",
                             letterSpacing: "0.04em" }}>
                changelog.rss
              </span>
            </a>
            <a
              href={`${API}/api/qelli/news/radar.rss`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-rss-news-radar"
              title="Subscribe to Qelli's News Radar — curated cyber/crypto/fintech headlines"
              className="inline-flex items-center gap-2 qt-mono text-[12px] transition-colors"
              style={{ color: "var(--qt-text-1)", textDecoration: "none",
                       letterSpacing: "0.04em" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--qt-cyan)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-1)")}
            >
              <Rss size={12} />
              <span>Qelli&apos;s News Radar</span>
              <span className="qt-mono text-[10px]"
                    style={{ color: "var(--qt-text-3)",
                             letterSpacing: "0.04em" }}>
                radar.rss
              </span>
            </a>
          </div>
        </div>

        {/* Bottom strip — copyright · domain · socials · lang switch ── */}
        <div className="mt-10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t"
             style={{ borderColor: "var(--qt-border)" }}>
          <span className="qt-mono text-sm" style={{ color: "var(--qt-text-3)" }}>
            © {new Date().getFullYear()} QorTrace · methodology {METHOD_VERSION}
          </span>
          <SocialRow testidPrefix="footer" />
          <div className="flex items-center gap-4">
            <span className="qt-mono text-sm hidden md:inline"
                  style={{ color: "var(--qt-text-3)" }}>
              qortrace.com
            </span>
            <LanguageSwitcher compact={true} />
          </div>
        </div>
      </div>
    </footer>
  );
}


// Iter275.3 — Email capture chip pinned under the brand block. Hits
// the existing threat-briefing newsletter route — no new backend
// endpoint needed. We treat any non-4xx response as success and clear
// the input to give the visitor a confident UX moment.
function NewsletterChip() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | sent | err
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "busy") return;
    setState("busy");
    setMsg(null);
    try {
      const r = await axios.post(`${API}/api/public/threat-briefing/subscribe`, {
        email, source: "footer-newsletter-chip",
      });
      setState("sent");
      setEmail("");
      // Iter273.2 — distinguish first-time vs already-subscribed so
      // testers don't think their second attempt errored.
      if (r?.data?.already_subscribed) {
        setMsg("You're already on the list. Welcome back.");
      } else {
        setMsg("You're on the list. Check your inbox for a welcome email — first Brief lands within 7 days.");
      }
    } catch (err) {
      setState("err");
      const status = err?.response?.status;
      if (status === 429) {
        setMsg("Too many signups from your network. Try again in a few minutes.");
      } else if (status === 400) {
        setMsg(err?.response?.data?.detail || "That email looks malformed — double-check it?");
      } else {
        setMsg("Couldn't subscribe — refresh the page and try again, or email hello@qortrace.com.");
      }
    }
  };

  return (
    <form onSubmit={submit}
          data-testid="footer-newsletter-form"
          className="mt-6 qt-card"
          style={{
            border: "1px solid var(--qt-border)",
            background: "linear-gradient(135deg, rgba(0,229,255,0.04), transparent 70%)",
            padding: "14px 16px",
            maxWidth: 420,
          }}>
      <div className="flex items-center gap-2 mb-2">
        <Radio size={14} style={{ color: "var(--qt-cyan)" }} />
        <span className="qt-mono text-[10px]"
              style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em",
                       fontWeight: 700 }}>
          QUANTUM THREAT BRIEF
        </span>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--qt-text-3)" }}>
        Weekly intel on PQC migration, NIST schedule drift, and onchain
        cryptographic risk. No fluff.
      </p>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          data-testid="footer-newsletter-input"
          className="flex-1"
          style={{
            background: "rgba(7,10,18,0.6)",
            border: "1px solid var(--qt-border)",
            color: "var(--qt-text-1)",
            padding: "8px 10px",
            borderRadius: 4, fontSize: 13,
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={state === "busy"}
          data-testid="footer-newsletter-submit"
          className="qt-btn qt-btn-primary"
          style={{
            fontSize: 12, padding: "0.55rem 0.85rem",
            opacity: state === "busy" ? 0.65 : 1,
          }}
        >
          {state === "busy" ? "Subscribing…" : (
            <>Subscribe <ArrowRight size={12} /></>
          )}
        </button>
      </div>
      {msg && (
        <div className="qt-mono text-[10px] mt-2"
             data-testid="footer-newsletter-msg"
             style={{
               color: state === "err" ? "#FF6E6E" : "#1FDD83",
               letterSpacing: "0.10em",
             }}>
          {msg.toUpperCase()}
        </div>
      )}
    </form>
  );
}


// Iter275.4 — The bottom-strip social row now lives in the shared
// `SocialRow.jsx` so it's reused by the mobile/tablet hamburger
// slideout (MobileNav.jsx). Footer just imports it.


function FooterCol({ title, children }) {
  return (
    <div>
      <div className="qt-label mb-3"
           style={{ color: "var(--qt-cyan)", fontWeight: 700 }}
           data-testid="footer-col-title">
        {title}
      </div>
      <ul className="space-y-2.5 leading-tight">{children}</ul>
    </div>
  );
}
function FooterLink({ to, children, testid, badge, external }) {
  const linkProps = external
    ? {
        href: to, target: "_blank", rel: "noopener noreferrer",
      }
    : {};
  const LinkTag = external ? "a" : Link;
  return (
    <li className="text-base leading-tight">
      <LinkTag
        {...(external ? linkProps : { to })}
        data-testid={testid}
        className="inline-block transition-colors"
        style={{
          color: "var(--qt-text-2)",
          paddingTop: 0, paddingBottom: 0,
          lineHeight: "1.4",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#FFFFFF")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-2)")}
      >
        {children}
        {badge && (
          <span
            className="qt-mono"
            style={{
              marginLeft: 6,
              fontSize: 9,
              letterSpacing: "0.12em",
              fontWeight: 700,
              padding: "1px 5px",
              borderRadius: 2,
              color: badge === "LIVE" ? "#FF2A2A"
                    : badge === "NEW"  ? "#1FDD83"
                                       : "var(--qt-cyan)",
              border: `1px solid ${
                badge === "LIVE" ? "#FF2A2A66"
                : badge === "NEW"  ? "#1FDD8366"
                                   : "var(--qt-border-strong)"}`,
              background: badge === "LIVE" ? "rgba(255,42,42,0.10)"
                          : badge === "NEW"  ? "rgba(31,221,131,0.10)"
                                               : "transparent",
              verticalAlign: "middle",
            }}
          >
            {badge}
          </span>
        )}
      </LinkTag>
    </li>
  );
}
function FooterMuted({ children }) {
  return (
    <li className="text-base leading-tight">
      <span
        className="inline-block transition-colors"
        style={{ color: "var(--qt-text-3)", lineHeight: "1.4" }}
      >
        {children}
      </span>
    </li>
  );
}
function TrustChip({ children }) {
  return (
    <span
      className="qt-mono text-[10px] px-2 py-1 rounded"
      style={{
        border: "1px solid var(--qt-border-strong)",
        color: "var(--qt-text-2)",
        letterSpacing: "0.1em",
      }}
    >
      {children}
    </span>
  );
}

// Iter312 — "Just shipped" bullet. Small green dot + linked label;
// matches the rest of the footer's text-sm cadence.
function ShippedItem({ to, children, testid, badge, external }) {
  const Tag = external ? "a" : Link;
  const extra = external ? { href: to, target: "_blank", rel: "noreferrer" } : { to };
  return (
    <li className="flex items-center gap-2 leading-tight">
      <span
        aria-hidden
        className="inline-block flex-shrink-0 rounded-full"
        style={{
          width: 6, height: 6, background: "#1FDD83",
          boxShadow: "0 0 6px rgba(31,221,131,0.55)",
        }}
      />
      <Tag
        {...extra}
        data-testid={testid}
        className="inline-block transition-colors"
        style={{ color: "var(--qt-text-2)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#FFFFFF")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--qt-text-2)")}
      >
        {children}
        {badge && (
          <span
            className="qt-mono ml-1.5 px-1 py-[1px] rounded text-[9px]"
            style={{
              background: "rgba(31,221,131,0.16)",
              color:      "#1FDD83",
              letterSpacing: "0.10em",
              verticalAlign: "1px",
            }}
          >
            {badge}
          </span>
        )}
      </Tag>
    </li>
  );
}

// Iter312 — backend-driven Just Shipped list. Reads top-3 release
// markers from /api/public/release-markers (sourced from the
// immutable audit chain). Falls back to hard-coded bullets if the
// API is unreachable so the section never goes blank.
function JustShipped() {
  const FALLBACK = [
    { id: "rel-live-trust-posture",   label: "Live Trust Posture + 90-day uptime trail", href: "/trust",         badge: "NEW" },
    { id: "rel-pqc-migration-diff",   label: "PQC Migration Diff Viewer",                href: "/account/cbom",  badge: ""    },
    { id: "rel-qday-tax-clock",       label: "Q-Day Tax Clock + embeddable widget",      href: "/q-day",         badge: ""    },
  ];
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/public/release-markers?limit=3`,
          { timeout: 6000 },
        );
        const remote = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled && remote.length > 0) setItems(remote);
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mt-4" data-testid="footer-just-shipped">
      <div
        className="qt-mono text-[10px] mb-1.5"
        style={{
          color: "var(--qt-cyan)",
          letterSpacing: "0.16em",
          fontWeight: 700,
        }}
      >
        JUST SHIPPED
      </div>
      <ul
        className="space-y-1 text-[12px]"
        style={{ color: "var(--qt-text-2)" }}
        data-testid="footer-just-shipped-list"
      >
        {items.map((it) => (
          <ShippedItem
            key={it.id}
            to={it.href}
            external={/^https?:\/\//.test(it.href || "")}
            testid={`footer-shipped-${it.id}`}
            badge={it.badge || ""}
          >
            {it.label}
          </ShippedItem>
        ))}
      </ul>
      <Link
        to="/releases"
        className="mt-2 inline-block text-[11px] underline-offset-2 hover:underline"
        style={{ color: "var(--qt-text-3)" }}
        data-testid="footer-shipped-view-all"
      >
        View full release log →
      </Link>
    </div>
  );
}
